import {Action, Selector, State, StateContext} from '@ngxs/store';
import {ValidationStateModel} from '../../modules/validation/models/validation-state.model';
import {Calculation} from '../../models/api/calculation.model';
import {GeneralContract} from '../../models/api/product/base.model';
import {CalculationEditActions} from './calculation-edit.actions';
import {inject, Injectable} from '@angular/core';
import {RiskDefinition} from '../../models/api/riskDefinition.model';
import {CalculationService} from '../../services/calculation.service';
import {catchError, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {ValidationActions} from '../../modules/validation/state/validation.actions';
import {NavItemCodes} from '../../enums/nav-item-codes.enum';
import {NavService} from '../../services/nav.service';
import {NotificationService} from '../../services/notification.service';
import {HttpErrorResponse} from '@angular/common/http';
import {BaseCalculationEditState} from './base-calculation-edit.state';
import {ProductTypeEnum} from '../../enums/product-type.enum';
import {GeneralContractGLMD, PolicyGLMD} from '../../models/api/product/glmd.model';
import {clearProperties} from '../../helpers/object.helper';

interface CalculationEditStateModel extends ValidationStateModel {
    originalCalculation?: Calculation;
    generalContract: GeneralContract;
    risksDefinition: RiskDefinition;
    activeCalculation?: Calculation;
    vehicleMakeList?: string[];
    premium?: number;
    blockSave?: boolean;
    validationMessage?: string;
}

@State<CalculationEditStateModel>({
    name: CalculationEditState.STATE_NAME,
    defaults: {
        generalContract: null,
        risksDefinition: null,
        originalCalculation: null,
        validationErrors: null,
        activeCalculation: null,
        vehicleMakeList: null,
        premium: null,
        validationMessage: null,
    },
})
@Injectable()
export class CalculationEditState extends BaseCalculationEditState {
    static readonly STATE_NAME = 'calculationEdit';

    protected calculationService = inject(CalculationService);
    protected navService = inject(NavService);
    protected notificationService = inject(NotificationService);

    @Selector()
    static activeCalculation(state: CalculationEditStateModel) {
        return state.activeCalculation;
    }

    @Selector()
    static risksDefinition(state: CalculationEditStateModel) {
        return state.risksDefinition;
    }

    @Selector()
    static generalContract(state: CalculationEditStateModel) {
        return state.generalContract;
    }

    @Selector()
    static originalCalculation(state: CalculationEditStateModel) {
        return state.originalCalculation;
    }

    @Selector()
    static vehicleMakeList(state: CalculationEditStateModel) {
        return state.vehicleMakeList;
    }

    @Selector()
    static validationErrors(state: CalculationEditStateModel) {
        return state.validationErrors;
    }

    @Selector()
    static premium(state: CalculationEditStateModel) {
        return state.premium;
    }

    @Selector()
    static blockSave(state: CalculationEditStateModel) {
        return state.blockSave;
    }

    @Selector()
    static validationMessage(state: CalculationEditStateModel) {
        return state.validationMessage;
    }

    @Action(CalculationEditActions.SetRisksDefinition)
    setRisksDefinition(
        ctx: StateContext<CalculationEditStateModel>,
        {risksDefinition}: CalculationEditActions.SetRisksDefinition
    ) {
        ctx.patchState({
            risksDefinition,
        });
    }

    @Action(CalculationEditActions.SetCalculationData)
    setCalculationData(
        ctx: StateContext<CalculationEditStateModel>,
        {generalContract, originalCalculation}: CalculationEditActions.SetCalculationData
    ) {
        const activeCalculation = this.mapCalculationData(generalContract, originalCalculation);
        ctx.patchState({
            generalContract,
            originalCalculation,
            activeCalculation,
        });
    }

    @Action(CalculationEditActions.ClearState)
    clearState(ctx: StateContext<CalculationEditStateModel>) {
        ctx.setState({
            generalContract: null,
            risksDefinition: null,
            originalCalculation: null,
            validationErrors: null,
            activeCalculation: null,
            premium: null,
            blockSave: false,
        });
    }

    @Action(CalculationEditActions.SetVehicleMakeList)
    setVehicleMakeList(
        ctx: StateContext<CalculationEditStateModel>,
        {vehicleMakeList}: CalculationEditActions.SetVehicleMakeList
    ) {
        ctx.patchState({vehicleMakeList});
    }

    @Action(CalculationEditActions.Calculate)
    calculate(ctx: StateContext<CalculationEditStateModel>, {body, productCode}: CalculationEditActions.Calculate) {
        return this.calculationService.calculate(body, productCode, this.getRequestOptions()).pipe(
            tap((res) => {
                ctx.patchState({premium: res.policy.premium, blockSave: false, validationMessage: null});
                ctx.dispatch(new ValidationActions.Clear(CalculationEditState.STATE_NAME));
            }),
            catchError((rej: HttpErrorResponse) => {
                ctx.patchState({blockSave: true});
                return this.handleCalculationError(ctx, rej);
            })
        );
    }

    @Action(CalculationEditActions.Save)
    save(ctx: StateContext<CalculationEditStateModel>, {body, productCode}: CalculationEditActions.Save) {
        return this.calculationService.saveCalculation(body, productCode, this.getRequestOptions()).pipe(
            tap((res) => {
                this.notificationService.success('CALCULATION.CREATED');
                this.navService.navigateByCode(NavItemCodes.CALCULATION_PREVIEW, {calculationId: res?.id});
            }),
            catchError((rej: HttpErrorResponse) => this.handleCalculationError(ctx, rej))
        );
    }

    @Action(CalculationEditActions.SetBlockSave)
    setBlockSave(ctx: StateContext<CalculationEditStateModel>, {blockSave}: CalculationEditActions.SetBlockSave) {
        ctx.patchState({
            blockSave,
        });
    }

    protected handleCalculationError(
        ctx: StateContext<CalculationEditStateModel>,
        rej: HttpErrorResponse
    ): Observable<null> {
        ctx.patchState({premium: null, validationMessage: this.getManualValidation(rej)});
        const messages = this.getValidationErrors(rej);
        ctx.dispatch(new ValidationActions.Set(CalculationEditState.STATE_NAME, messages));
        return of(null);
    }

    protected mapCalculationData(generalContract: GeneralContract, originalCalculation?: Calculation): Calculation {
        if (originalCalculation) {
            return this.copyOriginalCalculation(Object.freeze(generalContract), Object.freeze(originalCalculation));
        } else {
            return this.prepareNewCalculation(generalContract);
        }
    }

    override copyOriginalCalculation(
        generalContract: Readonly<GeneralContract>,
        originalCalculation: Calculation
    ): Calculation {
        const copy = super.copyOriginalCalculation(generalContract, originalCalculation);
        if (generalContract.productCode === ProductTypeEnum.GLMD) {
            if (
                !(generalContract as GeneralContractGLMD).vehicleCoOwnerAvailable &&
                (copy.policy as PolicyGLMD).vehicleCoOwners?.length
            ) {
                clearProperties(copy.policy as PolicyGLMD, ['vehicleCoOwners']);
            }
        }
        return copy;
    }
}
